import { useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';

import type { AuthUser } from '@repo/contracts';
import { EXT_AUTH_MESSAGE_SOURCE } from '@repo/contracts/extension-auth';
import {
  isTrustedExtensionId,
  TRUSTED_EXTENSION_IDS,
} from '@repo/contracts/extension-ids';

import { logger } from '../lib/logger';
import { useScopeStore } from '../store/scope-store';
import { extensionIdKeys, fetchTrustedExtensionIds } from './extension-ids-api';
import { getSessionToken } from './session-token';

const log = logger.child({ module: 'extension-auth-handoff' });

/**
 * The web-app half of the standalone issue-report extension's sign-in
 * handshake (`apps/issue-report-extension/src/background/auth.ts`). That
 * extension opened this very tab as `/login?source=extension&extId=<id>` and
 * is waiting on `chrome.runtime.onMessageExternal` — this is the only reach
 * back into it there is, since a content script would need a host
 * permission that extension deliberately doesn't have.
 *
 * Called once sign-in has actually completed (after `refreshSession()`), by
 * both `/login` and `/login/verify-link` — the two places a sign-in can
 * finish. Not `useEffect`-driven off `useAuth()`'s `user`: this page is also
 * reachable by someone already signed in who is not using the extension at
 * all, and firing on every mount would be an unprompted message toward
 * whatever `extId` a URL happened to carry.
 */
/**
 * Whether this visit is the extension's handshake.
 *
 * Exported so `/login` can answer it without re-parsing the URL, and because
 * what it gates is **not navigating away** — see the note in `useExtensionAuthHandoff`.
 */
export function useIsExtensionAuthFlow(): boolean {
  const [searchParams] = useSearchParams();
  return (
    searchParams.get('source') === 'extension' &&
    searchParams.get('extId') !== null
  );
}

export function useExtensionAuthHandoff() {
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();

  return async (user: AuthUser) => {
    if (searchParams.get('source') !== 'extension') return;
    const extId = searchParams.get('extId');
    const token = getSessionToken();
    if (!token) return;

    /**
     * Checked against a list, not merely required to exist.
     *
     * What is about to be sent is a 14-day session token, and the recipient is
     * named by a query parameter — so without this, any extension (or anyone who
     * can get a user to follow a link) could put its own id in the URL and be
     * handed a session off Floto's own real login page. Narrow, because this
     * runs only after a sign-in actually completes on this visit, but
     * phishing-shaped and not worth leaving open for the one comparison it
     * costs. See `TRUSTED_EXTENSION_IDS` for how to add an extension.
     *
     * The list comes from the server, because a test deployment lets an admin
     * add a local unpacked build's id (see `platformSettings`) — Chrome derives
     * that id from the load path, so none of them can be committed.
     *
     * **Fetched here rather than prefetched on mount**, and the ordering is the
     * whole reason: at `/login` the visitor is signed out, so an authenticated
     * fetch on mount would 401 — and a 4xx is never retried, so the cache would
     * hold that error and the handoff would silently fall back for exactly the
     * person it is meant to serve. By this line a sign-in has completed and
     * `getSessionToken()` above has already proved there is a token to send. The
     * extension is parked on `onMessageExternal` and one round trip is nothing
     * against that.
     */
    let allowed: readonly string[] = TRUSTED_EXTENSION_IDS;
    try {
      allowed = await queryClient.fetchQuery({
        queryFn: fetchTrustedExtensionIds,
        queryKey: extensionIdKeys.trusted(),
        // Per deployment and changed by hand, so it is worth re-reading once per
        // sign-in rather than caching for the session.
        staleTime: 0,
      });
    } catch (err) {
      /**
       * Falls back to the compiled list, never to an empty one. An empty list
       * would break the published extension for everybody the moment this
       * request times out — a far worse failure than not honouring somebody's
       * local build.
       */
      log.warn(
        { err },
        'could not read the extension allowlist; using the built-in one',
      );
    }

    /**
     * A refusal is logged, and that is not decoration.
     *
     * This used to be a bare `return`, which made the one failure this feature
     * actually has completely silent: the extension only opens a tab and then
     * waits on `onMessageExternal`, so its own console shows no request and no
     * error, and this page said nothing either. "It does not work, there is
     * nothing anywhere" was the whole bug report. An id that is not on the list
     * is the ordinary case for a local unpacked build — Chrome derives its id
     * from the load path — so the fix is almost always "add this id at
     * /admin/settings", and the line below is what says which id to add.
     *
     * The list is safe to log: `GET /auth/extension-ids` serves it to any
     * signed-in caller, and it names extensions rather than anything about the
     * user.
     */
    if (!isTrustedExtensionId(extId, allowed)) {
      log.warn(
        { allowed, extId },
        'refused to hand a session to an extension id that is not on the allowlist',
      );
      return;
    }

    // `chrome` only exists on a browser with an extension platform; nothing
    // about this page otherwise depends on it, so the reference is guarded
    // rather than typed globally.
    const runtime = (
      globalThis as { chrome?: { runtime?: { sendMessage?: unknown } } }
    ).chrome?.runtime;
    if (typeof runtime?.sendMessage !== 'function') return;

    // This tab's own zustand store rehydrates from localStorage on mount
    // (the persist middleware, same storage key `floto.scope` any other tab
    // in this browser writes to), so this reads whatever team was actually
    // selected when "Sign in" was clicked — not this fresh tab's own default.
    const teamId = useScopeStore.getState().selectedTeamId;

    /**
     * **The page must still be on `/login` when this runs.**
     *
     * `chrome.runtime.sendMessage` from a web page is only permitted from a URL
     * matching the extension's `externally_connectable.matches`, which is
     * `<origin>/login*` — and Chrome evaluates that against the document's
     * *current* URL, not the one the script was loaded on. A client-side
     * navigation to `/` therefore revokes the permission mid-flight, and the
     * error Chrome gives for it is verbatim "Could not establish connection.
     * Receiving end does not exist." — indistinguishable from an uninstalled
     * extension or an evicted service worker, which is exactly how it read.
     *
     * `/login` used to `navigate('/')` the moment a session appeared, in an
     * effect that did not wait for this. It now stays put for the extension
     * flow (`useIsExtensionAuthFlow`) and lets the extension close the tab,
     * which is also what the reference implementation does.
     *
     * Sent with retries, because one dropped message loses the whole sign-in.
     *
     * The failure is Chrome's own "Could not establish connection. Receiving end
     * does not exist.", and against an MV3 extension it usually does not mean
     * what it says: the extension is installed, the page matches its
     * `externally_connectable`, and its `onMessageExternal` listener is
     * registered — but the service worker was **evicted** while the user was
     * signing in (30 seconds of idle is all it takes, and a password plus 2FA is
     * longer than that), and Chrome answers rather than waking it. It presents
     * as intermittent, which is what made it read as a configuration problem.
     *
     * The extension holds itself awake across this window now
     * (`background/auth.ts`), so this is the second line of defence and not the
     * fix. It still earns its place: an attempt that lands while the worker is
     * starting can fail once and succeed a moment later, and the whole cost of
     * being wrong here is a lost session with nothing on screen to explain it.
     *
     * Nothing is retried for a *refusal* — that is a decision, not a dropped
     * message, and it is logged above.
     */
    const send = runtime.sendMessage as (
      id: string,
      message: unknown,
    ) => Promise<unknown>;

    const payload = {
      sessionToken: token,
      source: EXT_AUTH_MESSAGE_SOURCE,
      teamId,
      type: 'EXT_AUTH_SUCCESS',
      user: { displayName: user.displayName, email: user.email, uid: user.uid },
    };

    for (let attempt = 1; attempt <= SEND_ATTEMPTS; attempt += 1) {
      try {
        await send(extId, payload);
        return;
      } catch (err) {
        if (attempt === SEND_ATTEMPTS) {
          log.warn(
            { attempts: attempt, err, extId, path: window.location.pathname },
            "the extension never received the session — check `path`: a page outside the extension's externally_connectable matches (anything but /login*) is refused with this exact error",
          );
          return;
        }
        await pause(SEND_RETRY_MS * attempt);
      }
    }
  };
}

/** Three tries over about a second and a half. Enough to cover a worker
 *  starting up, short enough that a genuinely absent extension does not hold
 *  the sign-in page. */
const SEND_ATTEMPTS = 3;
const SEND_RETRY_MS = 400;

function pause(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}
